import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, TextField, Select, MenuItem, FormControl,
  InputLabel, Switch, FormControlLabel, IconButton, Paper, Chip, Grid, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Alert, Menu
} from '@mui/material';
import {
  DragIndicator, Delete, Edit, Add, ContentCopy, Save, Visibility, Link as LinkIcon,
  ArrowUpward, ArrowDownward, TextFields, Subject, AlternateEmail, Phone, Numbers,
  ArrowDropDownCircle, RadioButtonChecked, CheckBox, UploadFile, CalendarMonth,
  Language, Public, OndemandVideo, Gavel
} from '@mui/icons-material';
import { formsAPI } from '../../services/api';
import { useSnackbar } from '../../utils/notify';
import { v4 as uuidv4 } from '../../utils/uuid';
import ScholarSettings from '../../components/ScholarSettings';

const FIELD_TYPES = [
  { type: 'text', label: 'Short Text', icon: <TextFields /> },
  { type: 'textarea', label: 'Long Text', icon: <Subject /> },
  { type: 'email', label: 'Email', icon: <AlternateEmail /> },
  { type: 'phone', label: 'Phone', icon: <Phone /> },
  { type: 'number', label: 'Number', icon: <Numbers /> },
  { type: 'dropdown', label: 'Dropdown', icon: <ArrowDropDownCircle /> },
  { type: 'radio', label: 'Radio Buttons', icon: <RadioButtonChecked /> },
  { type: 'checkbox', label: 'Checkboxes', icon: <CheckBox /> },
  { type: 'file_upload', label: 'File Upload', icon: <UploadFile /> },
  { type: 'date', label: 'Date', icon: <CalendarMonth /> },
  { type: 'url', label: 'URL', icon: <Language /> },
  { type: 'country', label: 'Country', icon: <Public /> },
  { type: 'video', label: 'Video (YouTube)', icon: <OndemandVideo /> },
  { type: 'terms_acceptance', label: 'Terms & Conditions', icon: <CheckBox /> },
];

export default function FormBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(null);
  const [schema, setSchema] = useState({ sections: [], fields: [] });
  const [editField, setEditField] = useState(null);
  const [editSection, setEditSection] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [formSettings, setFormSettings] = useState({ title: '', description: '', deadline: '', is_active: false });
  const [allowDuplicates, setAllowDuplicates] = useState(false);
  const [sendConfirmation, setSendConfirmation] = useState(false);
  const [enableCaptcha, setEnableCaptcha] = useState(false);
  const [bannerUrl, setBannerUrl] = useState('');
  const [bgImageUrl, setBgImageUrl] = useState('');
  const [bgOpacity, setBgOpacity] = useState(15);
  const [deadlineTzLink, setDeadlineTzLink] = useState('');
  const [scholarSettings, setScholarSettings] = useState({});
  // For per-section "add field" menu
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [addMenuSection, setAddMenuSection] = useState('');

  useEffect(() => {
    formsAPI.get(id).then((r) => {
      const f = r.data.data;
      setForm(f);
      setFormSettings({
        title: f.title,
        description: f.description || '',
        deadline: f.deadline ? f.deadline.slice(0, 16) : '',
        is_active: f.is_active,
      });
      try {
        const s = typeof f.schema === 'string' ? JSON.parse(f.schema) : f.schema;
        setSchema({ sections: s.sections || [], fields: s.fields || [] });
      } catch(e) {
        setSchema({ sections: [], fields: [] });
      }
      // Parse settings for duplicate policy
      try {
        const settings = typeof f.settings === 'string' ? JSON.parse(f.settings) : (f.settings || {});
        setAllowDuplicates(!!settings.allow_duplicate_emails);
        setSendConfirmation(!!settings.send_submission_confirmation);
        setEnableCaptcha(!!settings.enable_captcha);
        setBannerUrl(settings.banner_url || '');
        setBgImageUrl(settings.bg_image_url || '');
        setBgOpacity(settings.bg_opacity || 15);
        setDeadlineTzLink(settings.deadline_tz_link || '');
        setScholarSettings({
          scholar_validation: settings.scholar_validation || 'none',
          linked_scholarship_form: settings.linked_scholarship_form || '',
          allow_self_fund: !!settings.allow_self_fund,
        });
      } catch(e) {}
    });
  }, [id]);

  const save = async () => {
    try {
      const settings = {
          allow_duplicate_emails: allowDuplicates,
          send_submission_confirmation: sendConfirmation,
          enable_captcha: enableCaptcha,
          banner_url: bannerUrl,
          bg_image_url: bgImageUrl,
          bg_opacity: bgOpacity,
          deadline_tz_link: deadlineTzLink,
          scholar_validation: scholarSettings.scholar_validation || 'none',
          linked_scholarship_form: scholarSettings.linked_scholarship_form || '',
          allow_self_fund: !!scholarSettings.allow_self_fund,
        };
      const data = {
        title: formSettings.title,
        description: formSettings.description,
        form_type: form.form_type,
        schema: JSON.stringify(schema),
        deadline: formSettings.deadline ? formSettings.deadline + ':00Z' : null,
        is_active: formSettings.is_active,
        settings: JSON.stringify(settings),
      };
      await formsAPI.update(id, data);
      enqueueSnackbar('Form saved', { variant: 'success' });
      setDirty(false);
    } catch (err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Save failed', { variant: 'error' });
    }
  };

  const addSection = () => {
    const newSection = { id: uuidv4(), title: 'New Section', order: schema.sections.length };
    setSchema((s) => ({ ...s, sections: [...s.sections, newSection] }));
    setEditSection(newSection);
    setDirty(true);
  };

  const addField = (type, sectionId) => {
    const newField = {
      id: uuidv4(),
      type,
      label: `New ${FIELD_TYPES.find((t) => t.type === type && true) && FIELD_TYPES.find((t) => t.type === type).label || 'Field'}`,
      placeholder: '',
      required: false,
      options: ['dropdown', 'radio', 'checkbox'].includes(type) ? ['Option 1', 'Option 2'] : [],
      validation: type === 'video' ? { video_url: '', has_response: true } : {},
      order: schema.fields.filter((f) => f.section_id === sectionId).length,
      section_id: sectionId || '',
      help_text: '',
      scholar_only: false,
    };
    setSchema((s) => ({ ...s, fields: [...s.fields, newField] }));
    setEditField(newField);
    setDirty(true);
    setAddMenuAnchor(null);
  };

  const updateField = (fieldId, updates) => {
    setSchema((s) => ({
      ...s,
      fields: s.fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)),
    }));
    setDirty(true);
  };

  const deleteField = (fieldId) => {
    setSchema((s) => ({ ...s, fields: s.fields.filter((f) => f.id !== fieldId) }));
    setDirty(true);
  };

  const duplicateField = (field) => {
    const dup = { ...field, id: uuidv4(), label: field.label + ' (copy)', order: field.order + 1 };
    setSchema((s) => ({ ...s, fields: [...s.fields, dup] }));
    setDirty(true);
  };

  const moveField = (fieldId, direction) => {
    setSchema((s) => {
      const fields = [...s.fields];
      const idx = fields.findIndex((f) => f.id === fieldId);
      if (idx < 0) return s;
      const swap = direction === 'up' ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= fields.length) return s;
      [fields[idx], fields[swap]] = [fields[swap], fields[idx]];
      return { ...s, fields: fields.map((f, i) => ({ ...f, order: i })) };
    });
    setDirty(true);
  };

  const moveFieldToSection = (fieldId, newSectionId) => {
    setSchema((s) => ({
      ...s,
      fields: s.fields.map((f) => f.id === fieldId ? { ...f, section_id: newSectionId } : f),
    }));
    setDirty(true);
  };

  const updateSection = (sectionId, updates) => {
    setSchema((s) => ({
      ...s,
      sections: s.sections.map((sec) => (sec.id === sectionId ? { ...sec, ...updates } : sec)),
    }));
    setDirty(true);
  };

  const deleteSection = (sectionId) => {
    setSchema((s) => ({
      ...s,
      sections: s.sections.filter((sec) => sec.id !== sectionId),
      fields: s.fields.filter((f) => f.section_id !== sectionId),
    }));
    setDirty(true);
  };

  const getFieldsForSection = (sectionId) =>
    schema.fields.filter((f) => f.section_id === sectionId).sort((a, b) => a.order - b.order);

  // Clipboard copy with fallback for HTTP
  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => enqueueSnackbar('Link copied!'));
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        enqueueSnackbar('Link copied!');
      } catch(e) {
        enqueueSnackbar('Copy failed — select and copy manually', { variant: 'warning' });
      }
      document.body.removeChild(textarea);
    }
  };

  if (!form) return null;

  const publicLink = `${window.location.origin}/apply/${id}`;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Form Builder</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Visibility />} onClick={() => window.open(publicLink, '_blank')}>
            Preview
          </Button>
          <Button variant="contained" startIcon={<Save />} onClick={save} disabled={!dirty}>Save</Button>
        </Box>
      </Box>

      {dirty && <Alert severity="warning" sx={{ mb: 2 }}>You have unsaved changes</Alert>}

      {/* Form settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Form Settings</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Form Title" value={formSettings.title}
                onChange={(e) => { setFormSettings({ ...formSettings, title: e.target.value }); setDirty(true); }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Description (supports HTML)" value={formSettings.description}
                multiline rows={3}
                helperText="Supports HTML: &lt;b&gt;bold&lt;/b&gt;, &lt;i&gt;italic&lt;/i&gt;, &lt;ul&gt;&lt;li&gt;bullets&lt;/li&gt;&lt;/ul&gt;, &lt;a href=&quot;...&quot;&gt;links&lt;/a&gt;"
                onChange={(e) => { setFormSettings({ ...formSettings, description: e.target.value }); setDirty(true); }} />
            </Grid>
            {form.form_type === 'scholarship' && (
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Application Deadline (UTC)" type="datetime-local" value={formSettings.deadline}
                  onChange={(e) => { setFormSettings({ ...formSettings, deadline: e.target.value }); setDirty(true); }}
                  InputLabelProps={{ shrink: true }}
                  helperText="Entered and stored in UTC (not your local time)." />
              </Grid>
            )}
            {form.form_type === 'scholarship' && (
              <Grid item xs={12} md={8}>
                <TextField fullWidth label="Timezone converter link (optional)" value={deadlineTzLink}
                  onChange={(e) => { setDeadlineTzLink(e.target.value); setDirty(true); }}
                  placeholder="https://www.timeanddate.com/worldclock/converter.html"
                  helperText="Shown next to the deadline so applicants can see it in their local time." />
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <FormControlLabel control={
                <Switch checked={formSettings.is_active}
                  onChange={(e) => { setFormSettings({ ...formSettings, is_active: e.target.checked }); setDirty(true); }} />
              } label="Active (accepting submissions)" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={
                <Switch checked={allowDuplicates}
                  onChange={(e) => { setAllowDuplicates(e.target.checked); setDirty(true); }} />
              } label="Allow duplicate email submissions" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={
                <Switch checked={sendConfirmation}
                  onChange={(e) => { setSendConfirmation(e.target.checked); setDirty(true); }} />
              } label="Send submission confirmation email" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={
                <Switch checked={enableCaptcha}
                  onChange={(e) => { setEnableCaptcha(e.target.checked); setDirty(true); }} />
              } label="Enable CAPTCHA" />
            </Grid>
            <Grid item xs={12}><Divider sx={{ my: 1 }} /><Typography variant="subtitle2" sx={{ mb: 1 }}>Form Branding</Typography></Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Banner Image URL" value={bannerUrl}
                onChange={(e) => { setBannerUrl(e.target.value); setDirty(true); }}
                helperText="Header banner shown at top of form" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Background Image URL" value={bgImageUrl}
                onChange={(e) => { setBgImageUrl(e.target.value); setDirty(true); }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Background Opacity (%)" type="number" value={bgOpacity}
                onChange={(e) => { setBgOpacity(parseInt(e.target.value) || 15); setDirty(true); }}
                inputProps={{ min: 0, max: 100 }} helperText="0 = invisible, 100 = fully visible" />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f5f6fa', p: 1.5, borderRadius: 1 }}>
                <LinkIcon color="action" />
                <TextField size="small" value={publicLink} fullWidth InputProps={{ readOnly: true }}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} />
                <Button size="small" variant="contained" onClick={() => copyToClipboard(publicLink)}>
                  Copy
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Scholar Settings (registration forms only) */}
      {form && form.form_type === 'registration' && (
        <ScholarSettings
          formId={id}
          formType={form.form_type}
          settings={scholarSettings}
          onSettingsChange={(s) => { setScholarSettings(s); setDirty(true); }}
        />
      )}

      <Grid container spacing={3}>
        {/* Field palette - left sidebar */}
        <Grid item xs={12} md={3}>
          <Card sx={{ position: 'sticky', top: 80 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>Add Fields</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Click to add to first section, or use the + button on each section
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {FIELD_TYPES.map((ft) => (
                  <Button key={ft.type} size="small" startIcon={ft.icon}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    onClick={() => addField(ft.type, (schema.sections[0] && schema.sections[0].id) || '')}>
                    {ft.label}
                  </Button>
                ))}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Button fullWidth variant="outlined" onClick={addSection} startIcon={<Add />}>Add Section</Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Form canvas */}
        <Grid item xs={12} md={9}>
          {schema.sections.sort((a, b) => a.order - b.order).map((section) => (
            <Paper key={section.id} sx={{ p: 2, mb: 2, border: '2px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{section.title}</Typography>
                <Box>
                  <Tooltip title="Add field to this section">
                    <IconButton size="small" color="primary"
                      onClick={(e) => { setAddMenuAnchor(e.currentTarget); setAddMenuSection(section.id); }}>
                      <Add fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => setEditSection(section)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => deleteSection(section.id)}><Delete fontSize="small" /></IconButton>
                </Box>
              </Box>

              {getFieldsForSection(section.id).map((field) => (
                <FieldCard key={field.id} field={field}
                  onEdit={() => setEditField(field)} onDelete={() => deleteField(field.id)}
                  onDuplicate={() => duplicateField(field)}
                  onMoveUp={() => moveField(field.id, 'up')} onMoveDown={() => moveField(field.id, 'down')} />
              ))}

              {getFieldsForSection(section.id).length === 0 && (
                <Box sx={{ py: 3, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 1, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    No fields yet — click the + button above to add one
                  </Typography>
                </Box>
              )}
            </Paper>
          ))}

          {schema.sections.length === 0 && (
            <Paper sx={{ p: 6, textAlign: 'center', border: '2px dashed #ccc' }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Start building your form by adding a section, then add fields to each section.
                Each section becomes a step in the multi-step applicant form.
              </Typography>
              <Button variant="contained" onClick={addSection} startIcon={<Add />}>Add First Section</Button>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Per-section "Add Field" menu */}
      <Menu anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)} onClose={() => setAddMenuAnchor(null)}>
        {FIELD_TYPES.map((ft) => (
          <MenuItem key={ft.type} onClick={() => addField(ft.type, addMenuSection)}
            sx={{ gap: 1.5 }}>
            {React.cloneElement(ft.icon, { sx: { fontSize: 18, color: 'text.secondary' } })}
            {ft.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Field editor dialog */}
      {editField && (
        <FieldEditorDialog field={editField} formType={form.form_type}
          onClose={() => setEditField(null)}
          onSave={(updates) => { updateField(editField.id, updates); setEditField(null); }}
          sections={schema.sections} />
      )}

      {/* Section editor dialog */}
      {editSection && (
        <Dialog open onClose={() => setEditSection(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Section</DialogTitle>
          <DialogContent sx={{ pt: '16px !important' }}>
            <TextField fullWidth label="Section Title" value={editSection.title}
              onChange={(e) => setEditSection({ ...editSection, title: e.target.value })} sx={{ mb: 2 }} />
            <TextField fullWidth label="Section Description (optional - supports HTML)" multiline rows={3}
              value={editSection.description || ''}
              onChange={(e) => setEditSection({ ...editSection, description: e.target.value })}
              helperText="Displayed below the section title. Supports HTML for bold, italic, lists, links." />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>After this section, go to</InputLabel>
              <Select label="After this section, go to"
                value={editSection.next_section || ''}
                onChange={(e) => setEditSection({ ...editSection, next_section: e.target.value })}>
                <MenuItem value="">Next section in order (default)</MenuItem>
                {schema.sections.filter((s) => s.id !== editSection.id).sort((a, b) => a.order - b.order)
                  .map((s) => <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>)}
                <MenuItem value="__submit__">Submit form</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              A radio field in this section marked for branching can override this based on the applicant's answer.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditSection(null)}>Cancel</Button>
            <Button variant="contained" onClick={() => {
              updateSection(editSection.id, { title: editSection.title, description: editSection.description || '', next_section: editSection.next_section || '' });
              setEditSection(null);
            }}>Save</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

function FieldCard({ field, onEdit, onDelete, onDuplicate, onMoveUp, onMoveDown }) {
  const ft = FIELD_TYPES.find((t) => t.type === field.type);
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1,
      transition: 'all 0.15s', '&:hover': { borderColor: 'primary.main', bgcolor: '#fafbfd', boxShadow: 1 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <IconButton size="small" onClick={onMoveUp} sx={{ p: 0.25 }}><ArrowUpward sx={{ fontSize: 16 }} /></IconButton>
        <DragIndicator sx={{ color: 'text.disabled', fontSize: 18 }} />
        <IconButton size="small" onClick={onMoveDown} sx={{ p: 0.25 }}><ArrowDownward sx={{ fontSize: 16 }} /></IconButton>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {(ft && ft.icon) && React.cloneElement(ft.icon, { sx: { fontSize: 16, color: 'text.secondary' } })}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{field.label}</Typography>
          {field.required && <Chip label="Required" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
          {field.scholar_only && <Chip label="Scholar only" size="small" color="secondary" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {(ft && ft.label)}
          {field.type === 'video' && (field.validation && field.validation.video_url) && ' • YouTube embedded'}
          {field.placeholder && field.type !== 'video' && ` • "${field.placeholder}"`}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.25 }}>
        <IconButton size="small" onClick={onDuplicate}><ContentCopy fontSize="small" /></IconButton>
        <IconButton size="small" onClick={onEdit}><Edit fontSize="small" /></IconButton>
        <IconButton size="small" color="error" onClick={onDelete}><Delete fontSize="small" /></IconButton>
      </Box>
    </Paper>
  );
}

function FieldEditorDialog({ field, onClose, onSave, sections, formType }) {
  const [f, setF] = useState({ ...field });
  const [optionsText, setOptionsText] = useState((field.options || []).join('\n'));
  const hasOptions = ['dropdown', 'radio', 'checkbox'].includes(f.type);

  const handleSave = () => {
    const updates = { ...f };
    if (hasOptions) updates.options = optionsText.split('\n').map((o) => o.trim()).filter(Boolean);
    onSave(updates);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Field</DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Label / Question" value={f.label}
              onChange={(e) => setF({ ...f, label: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Field Type</InputLabel>
              <Select value={f.type} label="Field Type"
                onChange={(e) => setF({ ...f, type: e.target.value })}>
                {FIELD_TYPES.map((ft) => (
                  <MenuItem key={ft.type} value={ft.type}>{ft.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Section</InputLabel>
              <Select value={f.section_id || ''} label="Section"
                onChange={(e) => setF({ ...f, section_id: e.target.value })}>
                <MenuItem value="">No section</MenuItem>
                {sections.map((s) => <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          {f.type !== 'video' && (
            <Grid item xs={12}>
              <TextField fullWidth label="Placeholder" value={f.placeholder || ''}
                onChange={(e) => setF({ ...f, placeholder: e.target.value })} />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField fullWidth label="Help Text" value={f.help_text || ''} multiline rows={2}
              onChange={(e) => setF({ ...f, help_text: e.target.value })} />
          </Grid>
          {hasOptions && (
            <Grid item xs={12}>
              <TextField fullWidth label="Options (one per line)" value={optionsText} multiline rows={4}
                onChange={(e) => setOptionsText(e.target.value)} helperText="Enter each option on a new line" />
            </Grid>
          )}
          {f.type === 'radio' && (
            <>
              <Grid item xs={12}>
                <FormControlLabel control={
                  <Switch checked={!!f.branch_enabled}
                    onChange={(e) => setF({ ...f, branch_enabled: e.target.checked })} />
                } label="Use this field for branching (jump to a section based on the answer)" />
              </Grid>
              {f.branch_enabled && optionsText.split('\n').map((o) => o.trim()).filter(Boolean).map((opt) => (
                <Grid item xs={12} key={opt}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{'If "' + opt + '" then go to'}</InputLabel>
                    <Select label={'If "' + opt + '" then go to'}
                      value={(f.branch_map && f.branch_map[opt]) || ''}
                      onChange={(e) => setF({ ...f, branch_map: Object.assign({}, f.branch_map, { [opt]: e.target.value }) })}>
                      <MenuItem value="">Use section default</MenuItem>
                      {sections.filter((s) => s.id !== f.section_id).map((s) => <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>)}
                      <MenuItem value="__submit__">Submit form</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              ))}
              {f.branch_enabled && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Options left as "Use section default" follow this section's normal next-section setting.
                  </Typography>
                </Grid>
              )}
            </>
          )}
          {f.type === 'terms_acceptance' && (
            <>
              <Grid item xs={12}>
                <TextField fullWidth label="Policy Title" placeholder="Terms & Conditions"
                  value={(f.validation && f.validation.policy_title) || ''}
                  onChange={(e) => setF({ ...f, validation: { ...f.validation, policy_title: e.target.value } })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Policy Text (supports HTML)" multiline rows={6}
                  value={(f.validation && f.validation.policy_text) || ''}
                  onChange={(e) => setF({ ...f, validation: { ...f.validation, policy_text: e.target.value } })}
                  helperText="Full text shown in popup. Supports HTML: <b>bold</b>, <i>italic</i>, <ul><li>bullets</li></ul>, <a href='...'>links</a>, <h3>headings</h3>. Use this OR Policy URL below." />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Policy URL (alternative to text)" placeholder="https://..."
                  value={(f.validation && f.validation.policy_url) || ''}
                  onChange={(e) => setF({ ...f, validation: { ...f.validation, policy_url: e.target.value } })} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel control={
                  <Switch checked={(f.validation && f.validation.require_open) !== false}
                    onChange={(e) => setF({ ...f, validation: { ...f.validation, require_open: e.target.checked } })} />
                } label="Require opening to enable checkbox" />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel control={
                  <Switch checked={(f.validation && f.validation.must_scroll) !== false}
                    disabled={(f.validation && f.validation.require_open) === false}
                    onChange={(e) => setF({ ...f, validation: { ...f.validation, must_scroll: e.target.checked } })} />
                } label="Require scrolling to bottom before accepting" />
              </Grid>
            </>
          )}
          {f.type === 'video' && (
            <>
              <Grid item xs={12}>
                <TextField fullWidth label="YouTube URL (single video)" placeholder="https://www.youtube.com/watch?v=..."
                  value={(f.validation && f.validation.video_url) || ''}
                  onChange={(e) => setF({ ...f, validation: { ...f.validation, video_url: e.target.value } })}
                  helperText="For a single video. Or use multiple URLs below for applicant selection." />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Multiple Video URLs (one per line)" multiline rows={4}
                  placeholder={"https://www.youtube.com/watch?v=abc\nhttps://www.youtube.com/watch?v=def\nhttps://www.youtube.com/watch?v=ghi"}
                  value={(f.validation && f.validation.video_urls || []).join('\n')}
                  onChange={(e) => setF({ ...f, validation: { ...f.validation, video_urls: e.target.value.split('\n').filter(function(u) { return u.trim(); }) } })}
                  helperText="One URL per line. Applicant will choose one to respond to." />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Video Titles (one per line, matching URLs above)" multiline rows={4}
                  placeholder={"Topic A: Introduction\nTopic B: Advanced\nTopic C: Special"}
                  value={(f.validation && f.validation.video_titles || []).join('\n')}
                  onChange={(e) => setF({ ...f, validation: { ...f.validation, video_titles: e.target.value.split('\n') } })}
                  helperText="Display titles for the dropdown. Must match the order of URLs above." />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel control={
                  <Switch checked={(f.validation && f.validation.has_response) !== false}
                    onChange={(e) => setF({ ...f, validation: { ...f.validation, has_response: e.target.checked } })} />
                } label="Include text response field below video" />
              </Grid>
            </>
          )}
          {f.type === 'file_upload' && (
            <>
              <Grid item xs={6}>
                <TextField fullWidth label="Max File Size (MB)" type="number"
                  value={(f.validation && f.validation.max_file_size_mb) || 10}
                  onChange={(e) => setF({ ...f, validation: { ...f.validation, max_file_size_mb: parseInt(e.target.value) } })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Allowed Extensions" placeholder="pdf,jpg,png"
                  value={((f.validation && f.validation.allowed_extensions) || []).join(',')}
                  onChange={(e) => setF({ ...f, validation: { ...f.validation, allowed_extensions: e.target.value.split(',').map((x) => x.trim()) } })} />
              </Grid>
            </>
          )}
          {(f.type === 'text' || f.type === 'textarea') && (
            <>
              <Grid item xs={6}>
                <TextField fullWidth label="Min Words" type="number"
                  value={(f.validation && f.validation.min_words) || ''}
                  onChange={(e) => setF({ ...f, validation: { ...f.validation, min_words: parseInt(e.target.value) || undefined } })}
                  helperText="Minimum word count" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Max Words" type="number"
                  value={(f.validation && f.validation.max_words) || ''}
                  onChange={(e) => setF({ ...f, validation: { ...f.validation, max_words: parseInt(e.target.value) || undefined } })}
                  helperText="Maximum word count" />
              </Grid>
            </>
          )}
          <Grid item xs={6}>
            <FormControlLabel control={<Switch checked={f.required} onChange={(e) => setF({ ...f, required: e.target.checked })} />}
              label="Required" />
          </Grid>
          {formType === 'registration' && (
            <Grid item xs={6}>
              <FormControlLabel control={<Switch checked={f.scholar_only || false}
                onChange={(e) => setF({ ...f, scholar_only: e.target.checked })} />}
                label="Scholar only" />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save Field</Button>
      </DialogActions>
    </Dialog>
  );
}
